import { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { Retry } from 'assets/svg';
import Cross from 'assets/svg/Cross';
import Down from 'assets/svg/Down';
import UpAltSvg from 'assets/svg/UpAlt';
import { capitalizeFirstLetterOfEachWord } from 'utils/helperMethods';
import { slugifyTestIdSegment } from 'constants/ppaTestIds';

type FilterRibbonProps = {
    allCheckedAttribute: any;
    allSelectedValueForAllAttribute: any;
    applyFilterObject: any;
    resetFilterObject: Function;
    showIsEditedRecords?: 'yes' | 'no' | 'all';
    onSubmitHandler: (obj: any) => void;
    parentClassName?: string;
    renderAttribute?: (key: string, attributeValue: string | number) => React.ReactNode;
    filterKeys?: {
        label: string;
        value: string;
        dimension: string;
    }[];
    /**
     * Root id. Children derive from it: `_summary`, `_chip_{key}`, `_panel`,
     * `_group_{key}`, `_group_{key}_reset_btn`, `_reset_all_btn`, `_save_btn`.
     */
    'data-testid'?: string;
};
const FilterRibbon = ({
    allCheckedAttribute,
    applyFilterObject,
    allSelectedValueForAllAttribute,
    resetFilterObject,
    showIsEditedRecords,
    onSubmitHandler,
    parentClassName = 'px-1',
    renderAttribute,
    filterKeys,
    'data-testid': dataTestId,
}: FilterRibbonProps) => {
    const [showPopup, setShowPopup] = useState(false);
    const filterRibbonRef = useRef<any>(null);
    const [tempApplyFilterObject, setTempApplyFilterObject] = useState<any>({});
    const [tempAllCheckedAttribute, setTempAllCheckedAttribute] = useState<any>({});
    const [tempAllSelectedValueForAllAttribute, setTempAllSelectedValueForAllAttribute] = useState<any>({});

    useEffect(() => {
        if (showPopup) {
            setTempAllCheckedAttribute(_.cloneDeep(allCheckedAttribute));
            setTempApplyFilterObject(_.cloneDeep(applyFilterObject));
            setTempAllSelectedValueForAllAttribute(_.cloneDeep(allSelectedValueForAllAttribute));
        }
    }, [showPopup]);

    const displayFilterLabelPairs = useMemo(() => {
        return Object.keys(allCheckedAttribute)
            .filter((key: string) => allCheckedAttribute[key]?.size)
            .map((key) => {
                return {
                    key: key,
                    name: filterKeys
                        ? filterKeys.find((filter: any) => filter.value === key)?.label
                        : capitalizeFirstLetterOfEachWord(key, '_'),
                    count: allCheckedAttribute[key]?.size,
                    items: Array.from(allCheckedAttribute[key]),
                };
            });
    }, [allCheckedAttribute]);

    const tempFilterLabelPairs = useMemo(() => {
        return Object.keys(tempAllCheckedAttribute)
            .filter((key: string) => tempAllCheckedAttribute[key]?.size)
            .map((key) => {
                return {
                    key: key,
                    name: capitalizeFirstLetterOfEachWord(key, '_'),
                    count: tempAllCheckedAttribute[key]?.size,
                    items: Array.from(tempAllCheckedAttribute[key]),
                };
            });
    }, [tempAllCheckedAttribute]);

    const handleClickOutside = (event: any) => {
        if (filterRibbonRef.current && !filterRibbonRef.current?.contains(event.target)) {
            setShowPopup(false);
        }
    };

    const removeSelectedFilter = (filterName: string, attributeName: string) => {
        const newTempAllCheckedAttribute = _.clone(tempAllCheckedAttribute);
        newTempAllCheckedAttribute[filterName].delete(attributeName);
        setTempAllCheckedAttribute(newTempAllCheckedAttribute);
        const newTempApplyObj = _.clone(tempApplyFilterObject);

        if (tempAllSelectedValueForAllAttribute[filterName]) {
            if (!newTempApplyObj[filterName]['exclusion']) newTempApplyObj[filterName]['exclusion'] = new Set();
            newTempApplyObj[filterName]['exclusion'].add(attributeName);
        } else {
            if (!newTempApplyObj[filterName]['inclusion']) newTempApplyObj[filterName]['inclusion'] = new Set();
            newTempApplyObj[filterName]['inclusion'].delete(attributeName);
        }

        setTempApplyFilterObject(newTempApplyObj);
    };

    const removeAllByFilterId = (filterName: string) => {
        setTempAllSelectedValueForAllAttribute({ ...tempAllSelectedValueForAllAttribute, filterName: false });

        const newApplyFilterObj = _.clone(tempApplyFilterObject);
        newApplyFilterObj[filterName] = {};
        setTempApplyFilterObject(newApplyFilterObj);

        const newTempAllCheckedAttribute = _.clone(tempAllCheckedAttribute);
        newTempAllCheckedAttribute[filterName] = new Set();
        setTempAllCheckedAttribute(newTempAllCheckedAttribute);
    };

    const onSaveChanges = () => {
        setShowPopup(false);
        const saveResult: any = {
            allCheckedAttribute: tempAllCheckedAttribute,
            allSelectedValueForAllAttribute: tempAllSelectedValueForAllAttribute,
            applyFilterObject: tempApplyFilterObject,
        };
        onSubmitHandler(saveResult);
    };

    const onResetAll = () => {
        setShowPopup(false);
        resetFilterObject();
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const noFilters = displayFilterLabelPairs.length === 0;

    return (
        <div className={`w-full   relative ${parentClassName}`} ref={filterRibbonRef} data-testid={dataTestId}>
            <div
                data-testid={dataTestId ? `${dataTestId}_summary` : undefined}
                data-state-expanded={showPopup ? 'true' : undefined}
                data-state-empty={noFilters ? 'true' : undefined}
                className={`flex justify-between items-center bg-bgBackground rounded-md px-2 relative ${
                    noFilters ? '' : 'cursor-pointer'
                }  `}
                onClick={() => !noFilters && setShowPopup(!showPopup)}
            >
                <div className="h-10 flex items-center overflow-x-auto w-full whitespace-nowrap">
                    {showIsEditedRecords === 'yes' && (
                        <div className="flex gap-2 text-purpleFont font-medium mr-2">
                            <span>Edited Records</span>
                            <span className="text-black">{displayFilterLabelPairs.length ? '|' : ''}</span>
                        </div>
                    )}
                    {showIsEditedRecords === 'no' && (
                        <div className="flex gap-2 text-purpleFont font-medium mr-2">
                            <span>Non Edited Records</span>
                            <span className="text-black">{displayFilterLabelPairs.length ? '|' : ''}</span>
                        </div>
                    )}
                    {!noFilters ? (
                        <>
                            {displayFilterLabelPairs?.map((fl: any, index: number) => (
                                <div
                                    className="flex gap-2 text-purpleFont font-medium mr-2"
                                    key={fl.key}
                                    data-testid={
                                        dataTestId
                                            ? `${dataTestId}_chip_${slugifyTestIdSegment(String(fl.key))}`
                                            : undefined
                                    }
                                >
                                    <span>{`${fl.name} (${fl.count})`}</span>
                                    <span className="text-black">
                                        {index === displayFilterLabelPairs.length - 1 ? '' : '|'}
                                    </span>
                                </div>
                            ))}
                        </>
                    ) : (
                        <>
                            {(!showIsEditedRecords || showIsEditedRecords === 'all') && (
                                <span className="text-purpleFont">
                                    No filters applied. Apply filters to see them here.
                                </span>
                            )}
                        </>
                    )}
                </div>
                {!noFilters && <>{showPopup ? <UpAltSvg /> : <Down />}</>}
            </div>

            {/* POP UP CONTAINER */}
            {showPopup && (
                <div
                    data-testid={dataTestId ? `${dataTestId}_panel` : undefined}
                    className="absolute bg-white rounded-md z-10 w-full flex flex-col shadow-xl left-[-1px] top-[42px] p-3"
                >
                    <div className="flex justify-between  mt-2 border-b  border-backgroundGreyAlt4">
                        <div className="flex flex-col ">
                            <span className="font-xs text-black">Filter Selection</span>
                            <span className="font-xxs text-promoColour">
                                Click 'Save Changes' to apply any edits you make.
                            </span>
                        </div>
                        <span onClick={() => setShowPopup(false)}>
                            <Cross />
                        </span>
                    </div>
                    <div className="h-56 border-b  border-backgroundGreyAlt4 overflow-auto py-2 pr-1">
                        {tempFilterLabelPairs.map((filter: any, index: number) => {
                            const groupSlug = slugifyTestIdSegment(String(filter.key));
                            return (
                                <div
                                    key={filter.key}
                                    className="flex flex-col gap-1"
                                    data-testid={dataTestId ? `${dataTestId}_group_${groupSlug}` : undefined}
                                >
                                    <span className="text-promoColour  font-xxs"> {filter.name}</span>
                                    <div className="flex justify-between items-start">
                                        <div className="flex flex-wrap flex-grow">
                                            {filter.items?.map((attr: string) => (
                                                <div
                                                    key={attr}
                                                    data-testid={
                                                        dataTestId
                                                            ? `${dataTestId}_group_${groupSlug}_value_${slugifyTestIdSegment(
                                                                  String(attr),
                                                              )}`
                                                            : undefined
                                                    }
                                                    className="flex justify-center gap-2 items-center mr-3 bg-bgBackground text-purpleFont rounded pl-2  mb-3 font-xxs font-norma"
                                                >
                                                    {renderAttribute ? (
                                                        renderAttribute(filter.key, attr)
                                                    ) : (
                                                        <span className="py-1.5">{attr}</span>
                                                    )}
                                                    <span
                                                        data-testid={
                                                            dataTestId
                                                                ? `${dataTestId}_group_${groupSlug}_value_${slugifyTestIdSegment(
                                                                      String(attr),
                                                                  )}_remove_btn`
                                                                : undefined
                                                        }
                                                        className="cursor-pointer pr-2 py-1.5 h-full flex items-center hover:scale-105"
                                                        onClick={() => removeSelectedFilter(filter.key, attr)}
                                                    >
                                                        <Cross />
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                        <div
                                            data-testid={
                                                dataTestId ? `${dataTestId}_group_${groupSlug}_reset_btn` : undefined
                                            }
                                            className="px-1 p-1 border border-borderGreyAl1 rounded min-w-[80px] flex justify-around items-center font-normal cursor-pointer"
                                            onClick={() => removeAllByFilterId(filter.key)}
                                        >
                                            <Retry /> <span>Reset</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex justify-end items-end gap-5 h-12">
                        <div
                            data-testid={dataTestId ? `${dataTestId}_reset_all_btn` : undefined}
                            className="px-3 p-1 gap-2 border border-borderGreyAl1 rounded   flex justify-around items-center font-xs  cursor-pointer"
                            onClick={onResetAll}
                        >
                            <Retry /> <span>Reset All</span>
                        </div>
                        <div
                            data-testid={dataTestId ? `${dataTestId}_save_btn` : undefined}
                            className="bg-purpleFont font-xs cursor-pointer py-1 px-3 text-white rounded"
                            onClick={onSaveChanges}
                        >
                            Save Changes
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilterRibbon;
